import {
  Component,
  ElementRef,
  HostListener,
  computed,
  effect,
  input,
  model,
  output,
  signal,
  untracked,
  viewChild
} from '@angular/core';
import { takeUntilDestroyed } from '@angular/core/rxjs-interop';
import { FormsModule } from '@angular/forms';
import { Subject, debounceTime } from 'rxjs';

import { isNil } from '@gfe/core/utils/shared-utils';

import { ICONS } from '@core/services/icons/icons.service';
import { foldForSearch } from '@core/utils/search-text.utils';

import { isEditableTarget } from '@shared/utils/editable-target.utils';

export type QualifierType = 'text' | 'enum' | 'range' | 'date' | 'regex' | 'glob';

export interface QualifierConfig {
  type: QualifierType;
  description: string;
  values?: string[];
  operators?: string[];
  supportsRegex?: boolean;
  supportsGlob?: boolean;
}

export interface QualifiersConfig {
  [key: string]: QualifierConfig;
}

interface ParsedTokenBase {
  raw: string;
  start: number;
  end: number;
}
export interface ParsedTokenText extends ParsedTokenBase {
  type: 'text';
  value: string;
}
export interface ParsedTokenQualifier extends ParsedTokenBase {
  type: 'qualifier';
  key: string;
  negated: boolean;
  value: string;
}
export interface ParsedTokenOperator extends ParsedTokenBase {
  type: 'operator';
  operator: 'AND' | 'OR';
}
export interface ParsedTokenGroup extends ParsedTokenBase {
  type: 'group-start' | 'group-end';
}
export interface ParsedTokenRegex extends ParsedTokenBase {
  type: 'regex';
  key: string;
  negated: boolean;
  value: string;
}
export type ParsedToken =
  | ParsedTokenText
  | ParsedTokenQualifier
  | ParsedTokenOperator
  | ParsedTokenGroup
  | ParsedTokenRegex;

export interface QualifierValue {
  value: string;
  negated: boolean;
}

export interface SearchResult {
  rawQuery: string;
  tokens: ParsedToken[];
  qualifiers: Record<string, QualifierValue[]>;
  regexes: Record<string, QualifierValue[]>;
  textTerms: string[];
  hasLogicalOperators: boolean;
  groups: number;
  implicitAnd: boolean;
}

export interface Suggestion {
  type: 'qualifier' | 'value' | 'operator' | 'example';
  text: string;
  qualifier?: string;
  description: string;
}

@Component({
  selector: 'app-search-query-builder',
  imports: [FormsModule],
  templateUrl: './search-query-builder.component.html',
  styleUrl: './search-query-builder.component.css'
})
export class SearchQueryBuilderComponent {
  searchInput = viewChild<ElementRef<HTMLInputElement>>('searchInput');
  textZone = viewChild<ElementRef<HTMLDivElement>>('textZone');

  private searchSubject = new Subject<void>();

  query = signal<string>('');
  cursorPosition = signal<number>(0);
  selectedSuggestion = signal<number>(-1);
  showSuggestions = signal<boolean>(false);

  parsedTokens = computed(() => this.parseQuery(this.query(), this.validKeys()));
  suggestions = computed(() =>
    this.generateSuggestions(this.query(), this.cursorPosition(), this.qualifiers())
  );
  highlightedQuery = computed(() =>
    this.generateHighlightedQuery(this.query(), this.validKeys())
  );
  validKeys = computed(() => new Set(Object.keys(this.qualifiers())));

  instant = input<boolean>(false);
  qualifiers = input<QualifiersConfig>({});
  placeholder = input<string>('Search...');
  class = input<string>();

  searchQuery = output<SearchResult>();
  queryChange = output<string>();

  queryValue = model<string>('');

  constructor() {
    // Set up debounced search emissions
    this.searchSubject.pipe(debounceTime(300), takeUntilDestroyed()).subscribe(() => {
      this.emitSearchResult();
    });

    effect(() => {
      const queryValue = this.queryValue();
      const query = untracked(() => this.query());

      const searchInput = this.searchInput();
      if (
        !isNil(searchInput?.nativeElement) &&
        searchInput.nativeElement !== document.activeElement
      ) {
        searchInput.nativeElement.focus();
      }

      if (queryValue === query) return;

      this.query.set(queryValue);

      if (this.instant()) {
        this.searchSubject.next();
      }
    });

    effect(() => {
      const currentQuery = this.query();
      this.queryChange.emit(currentQuery);
    });
  }

  updateSearchQuery(newQuery: string): void {
    this.queryValue.set(newQuery);
  }

  private generateHighlightedQuery(queryString: string, validKeys: Set<string>): string {
    if (!queryString) return '';

    const tokens = this.parseQuery(queryString, validKeys);
    let result = '';
    let lastIndex = 0;

    tokens.forEach((token) => {
      if (token.start > lastIndex) {
        const beforeText = queryString.substring(lastIndex, token.start);
        result += this.escapeHtml(beforeText);
      }

      if ((token.type === 'qualifier' || token.type === 'regex') && token.key) {
        // For labels, don't show the - prefix since negation is in the value
        if (token.negated && token.key !== 'labels') {
          result += `<span class="text-red-600 font-medium">-</span>`;
        }

        const keyPart = token.key + ':';
        result += `<span class="text-zinc-900 dark:text-zinc-100">${this.escapeHtml(keyPart)}</span>`;

        const rawValue = token.raw.split(':')[1] || token.value!;
        const hasQuotes = rawValue.startsWith('"');
        const hasRegex = rawValue.startsWith('/');
        const valuePart = hasQuotes ? `"${token.value}"` : token.value;

        if (hasRegex) {
          result += `<span class="bg-green-600/10 text-green-600 py-1 rounded">${this.escapeHtml(rawValue)}</span>`;
        } else if (token.key === 'labels') {
          // Enhanced label highlighting based on operator type
          const labelValue = token.value!;

          // Detect operator for styling
          let operatorClass = 'bg-blue-500/10 text-blue-600'; // default for EQUALS

          if (labelValue.includes('!IN:')) {
            operatorClass = 'bg-red-500/10 text-red-600'; // NOT_IN
          } else if (labelValue.includes('IN:')) {
            operatorClass = 'bg-purple-500/10 text-purple-600'; // IN
          } else if (labelValue.includes('!=')) {
            operatorClass = 'bg-red-500/10 text-red-600'; // NOT_EQUALS
          } else if (labelValue.includes('>=') || labelValue.includes('<=')) {
            operatorClass = 'bg-orange-500/10 text-orange-600'; // GREATER_THAN_OR_EQUALS / LESS_THAN_OR_EQUALS
          } else if (labelValue.includes('>') || labelValue.includes('<')) {
            operatorClass = 'bg-yellow-500/10 text-yellow-600'; // GREATER_THAN / LESS_THAN
          }
          // else uses default blue for EQUALS

          result += `<span class="${operatorClass} py-1 rounded font-medium">${this.escapeHtml(valuePart)}</span>`;
        } else if (token.negated) {
          result += `<span class="bg-red-500/10 text-red-600 py-1 rounded font-medium">${this.escapeHtml(valuePart)}</span>`;
        } else {
          result += `<span class="bg-blue-500/10 text-blue-600 py-1 rounded font-medium">${this.escapeHtml(valuePart)}</span>`;
        }
      } else if (token.type === 'operator') {
        result += `<span class="text-purple-600">${this.escapeHtml(token.raw)}</span>`;
      } else if (token.type === 'group-start' || token.type === 'group-end') {
        result += `<span class="text-orange-600">${this.escapeHtml(token.raw)}</span>`;
      } else {
        result += `<span class="text-zinc-900 dark:text-zinc-100">${this.escapeHtml(token.raw)}</span>`;
      }

      lastIndex = token.end;
    });

    if (lastIndex < queryString.length) {
      result += this.escapeHtml(queryString.substring(lastIndex));
    }

    return result;
  }

  private escapeHtml(text: string): string {
    const div = document.createElement('div');
    div.textContent = text;
    return div.innerHTML;
  }

  /**
   * Detects the label operator from a label value string. Returns the operator string and
   * whether the condition is negated.
   *
   * Order matters - checks longer operators first to avoid partial matches:
   *
   * - Check "!in:" before "!="
   * - Check ">=" and "<=" before ">" and "<"
   * - Check "in:" separately
   *
   * @param value - The label value string (e.g., "env=prod", "envIN:a,b,c")
   * @returns Object with operator string and negated boolean, or null if invalid
   */
  private detectLabelOperator(
    value: string
  ): { operator: string; negated: boolean } | null {
    // Order matters! Check longer operators first
    if (value.includes('!IN:')) {
      return { operator: '!IN:', negated: true };
    }
    if (value.includes('IN:')) {
      return { operator: 'IN:', negated: false };
    }
    if (value.includes('!=')) {
      return { operator: '!=', negated: true };
    }
    if (value.includes('>=')) {
      return { operator: '>=', negated: false };
    }
    if (value.includes('<=')) {
      return { operator: '<=', negated: false };
    }
    if (value.includes('>')) {
      return { operator: '>', negated: false };
    }
    if (value.includes('<')) {
      return { operator: '<', negated: false };
    }
    if (value.includes('=')) {
      return { operator: '=', negated: false };
    }

    return null; // No valid operator found
  }

  private parseQuery(queryString: string, validKeys: Set<string>): ParsedToken[] {
    const tokens: ParsedToken[] = [];

    const queryTokensRegex =
      /(-?\w+):(("(?:[^"\\]|\\.)*"[^\s()]*|\/(?:[^/\\]|\\.)*\/|[^\s()]+)?)|(\bAND\b|\bOR\b)|(\()|(\))|("(?:[^"\\]|\\.)*"|[^\s()]+)/g;

    let match: RegExpExecArray | null;

    while ((match = queryTokensRegex.exec(queryString)) !== null) {
      if (match[1]) {
        const keyRaw = match[1];
        const key = keyRaw.replace(/^-/, '');
        let negated = keyRaw.startsWith('-');
        const rawValue = match[2] ?? '';
        const isRegex = rawValue.startsWith('/') && rawValue.endsWith('/');

        // Handle quoted portion followed by operators (e.g., "key with spaces"=value)
        let value = rawValue;
        if (value.startsWith('"')) {
          // Find the closing quote, handling escaped quotes
          let i = 1;
          while (i < value.length) {
            if (value[i] === '"' && (i === 0 || value[i - 1] !== '\\')) {
              // Found unescaped closing quote
              const quotedPart = value.substring(1, i);
              const afterQuote = value.substring(i + 1);
              value = quotedPart + afterQuote;
              break;
            }
            i++;
          }
          // If no closing quote found, just remove opening quote
          if (i === value.length) {
            value = value.substring(1);
          }
        } else if (value.startsWith('/') && value.endsWith('/')) {
          // Handle regex values
          value = value.substring(1, value.length - 1);
        }

        // Special handling for 'labels' qualifier - negation is based on operator in value
        if (key === 'labels' && validKeys.has(key)) {
          const operatorInfo = this.detectLabelOperator(value);

          if (operatorInfo === null) {
            // Invalid label format (no operator) - treat as text token
            tokens.push({
              type: 'text',
              value: match[0],
              raw: match[0],
              start: match.index,
              end: match.index + match[0].length
            });
            continue;
          }

          negated = operatorInfo.negated;
          // Keep the full value including the operator for labels
          // value already contains the operator (e.g., "env=prod", "env>10", "tier in:a,b,c")
        }

        if (validKeys.has(key)) {
          tokens.push({
            type: isRegex ? 'regex' : 'qualifier',
            negated,
            key,
            value,
            raw: match[0],
            start: match.index,
            end: match.index + match[0].length
          });
        } else {
          tokens.push({
            type: 'text',
            value: match[0],
            raw: match[0],
            start: match.index,
            end: match.index + match[0].length
          });
        }
      } else if (match[4]) {
        tokens.push({
          type: 'operator',
          operator: match[4] as 'AND' | 'OR',
          raw: match[0],
          start: match.index,
          end: match.index + match[0].length
        });
      } else if (match[5]) {
        tokens.push({
          type: 'group-start',
          raw: match[0],
          start: match.index,
          end: match.index + match[0].length
        });
      } else if (match[6]) {
        tokens.push({
          type: 'group-end',
          raw: match[0],
          start: match.index,
          end: match.index + match[0].length
        });
      } else if (match[7]) {
        const cleaned = match[7].replace(/^"|"$/g, '').replace(/^\/|\/$/g, '');
        tokens.push({
          type: 'text',
          value: cleaned,
          raw: match[0],
          start: match.index,
          end: match.index + match[0].length
        });
      }
    }

    return tokens;
  }

  private generateSuggestions(
    queryString: string,
    cursorPos: number,
    qualifiers: QualifiersConfig
  ): Suggestion[] {
    const beforeCursor = queryString.substring(0, cursorPos);

    const qualifierMatch = beforeCursor.match(/(\S*)$/);
    const currentWord = qualifierMatch ? qualifierMatch[1] : '';

    const valueMatch = beforeCursor.match(/(\w+):(\S*)$/);

    if (valueMatch) {
      const qualifier = valueMatch[1];
      const partialValue = valueMatch[2];

      if (qualifiers[qualifier]) {
        const config = qualifiers[qualifier];

        if (config.type === 'enum' && config.values) {
          return config.values
            .filter((v) => foldForSearch(v).includes(foldForSearch(partialValue)))
            .map((v) => ({
              type: 'value',
              text: v,
              qualifier: qualifier,
              description: `${qualifier}:${v}`
            }));
        } else if (config.type === 'range' && config.operators) {
          return config.operators.map((op) => ({
            type: 'operator',
            text: op,
            qualifier: qualifier,
            description: `${qualifier}:${op}...`
          }));
        }
      }
      return [];
    }

    if (currentWord.includes(':') || currentWord.length > 0) {
      const searchTerm = currentWord.replace(/:$/, '');
      return Object.entries(qualifiers)
        .filter(([key]) => foldForSearch(key).includes(foldForSearch(searchTerm)))
        .map(([key, config]) => ({
          type: 'qualifier',
          text: `${key}:`,
          description: config.description
        }));
    }

    return [];
  }

  onInputChange(event: Event): void {
    const input = event.target as HTMLInputElement;
    this.queryValue.set(input.value);
    this.cursorPosition.set(input.selectionStart ?? 0);
    this.showSuggestions.set(true);
  }

  onKeyDown(event: KeyboardEvent): void {
    const suggs = this.suggestions();

    if (!this.showSuggestions() || suggs.length === 0) {
      if (event.key === 'Enter') {
        this.emitSearchResult();
      }
      return;
    }

    switch (event.key) {
      case 'ArrowDown':
        event.preventDefault();
        this.selectedSuggestion.update((prev) => (prev < suggs.length - 1 ? prev + 1 : 0));
        break;
      case 'ArrowUp':
        event.preventDefault();
        this.selectedSuggestion.update((prev) => (prev > 0 ? prev - 1 : suggs.length - 1));
        break;
      case 'Enter':
      case 'Tab':
        event.preventDefault();
        this.applySuggestion(suggs[this.selectedSuggestion()]);
        break;
      case 'Escape':
        this.showSuggestions.set(false);
        break;
    }
  }

  onFocus(): void {
    this.showSuggestions.set(true);
  }

  onBlur(): void {
    setTimeout(() => this.showSuggestions.set(false), 200);
  }

  applySuggestion(suggestion: Suggestion): void {
    const queryString = this.query();
    const cursorPos = this.cursorPosition();
    const beforeCursor = queryString.substring(0, cursorPos);
    const afterCursor = queryString.substring(cursorPos);

    let newQuery: string;
    let newCursorPos: number;

    if (suggestion.type === 'qualifier') {
      const match = beforeCursor.match(/(\S*)$/);
      const replaceStart = (match ? match.index : cursorPos) ?? 0;
      newQuery = queryString.substring(0, replaceStart) + suggestion.text + afterCursor;
      newCursorPos = replaceStart + suggestion.text.length;
    } else if (suggestion.type === 'value' || suggestion.type === 'example') {
      const match = beforeCursor.match(/(\w+:)(\S*)$/);
      if (match) {
        const replaceStart = match.index! + match[1].length;

        if (suggestion.text.includes(' ')) {
          suggestion.text = `"${suggestion.text}"`;
        }

        newQuery =
          queryString.substring(0, replaceStart) + suggestion.text + ' ' + afterCursor;
        newCursorPos = replaceStart + suggestion.text.length + 1;
      } else {
        return;
      }
    } else if (suggestion.type === 'operator') {
      const match = beforeCursor.match(/(\w+:)(\S*)$/);
      if (match) {
        const replaceStart = match.index! + match[1].length;
        newQuery = queryString.substring(0, replaceStart) + suggestion.text + afterCursor;
        newCursorPos = replaceStart + suggestion.text.length;
      } else {
        return;
      }
    } else {
      return;
    }

    this.queryValue.set(newQuery);
    this.showSuggestions.set(false);

    setTimeout(() => {
      const input = this.searchInput()?.nativeElement;
      if (input) {
        input.focus();
        input.setSelectionRange(newCursorPos, newCursorPos);
        this.cursorPosition.set(newCursorPos);
      }
    }, 0);
  }

  removeToken(token: ParsedToken): void {
    const queryString = this.query();
    const newQuery = (
      queryString.substring(0, token.start) + queryString.substring(token.end)
    ).trim();
    this.queryValue.set(newQuery);
  }

  clearQuery(): void {
    this.queryValue.set('');
  }

  private emitSearchResult(): void {
    const parsed = this.parsedTokens();
    let hasLogicalOperators = false;
    let groupCount = 0;

    const result: SearchResult = {
      rawQuery: this.query(),
      tokens: parsed,
      qualifiers: {},
      regexes: {},
      textTerms: [],
      hasLogicalOperators: false,
      groups: 0,
      implicitAnd: false
    };

    parsed.forEach((token) => {
      if (token.type === 'qualifier' && token.key) {
        if (!token.value) return;

        if (!result.qualifiers[token.key]) {
          result.qualifiers[token.key] = [];
        }
        result.qualifiers[token.key].push({
          value: token.value!,
          negated: token.negated ?? false
        });
      } else if (token.type === 'text' && token.value) {
        result.textTerms.push(token.value);
      } else if (token.type === 'operator') {
        hasLogicalOperators = true;
      } else if (token.type === 'group-start') {
        groupCount++;
      } else if (token.type === 'regex') {
        if (!result.regexes[token.key]) {
          result.regexes[token.key] = [];
        }
        result.regexes[token.key].push({
          value: token.value!,
          negated: token.negated ?? false
        });
      }
    });

    result.hasLogicalOperators = hasLogicalOperators;
    result.groups = groupCount;

    const termCount = Object.keys(result.qualifiers).length + result.textTerms.length;
    result.implicitAnd = !hasLogicalOperators && termCount > 1;

    this.searchQuery.emit(result);
  }

  protected onScrollSync(event: Event): void {
    const input = event.target as HTMLInputElement;
    if (this.textZone()?.nativeElement) {
      this.textZone()!.nativeElement.scrollLeft = input.scrollLeft;
    }
  }

  @HostListener('document:keydown', ['$event'])
  protected onGlobalKeydown(event: KeyboardEvent): void {
    if (event.key !== '/' || isEditableTarget(event.target)) {
      return;
    }
    event.preventDefault();
    this.searchInput()?.nativeElement.focus();
  }

  protected readonly ICONS = ICONS;
}
